/**
 * V4 — Deterministic multi-round repo context retrieval with sufficiency checks.
 */

import {
  buildRepoContext,
  type RepoContextBuildInput,
  type RepoContextBuildResult,
} from '@/services/githubRepoContextRouter';
import { searchRepo } from '@/services/repoRagService';

export type RepoTaskKind = 'edit' | 'debug' | 'explain';

export interface AgenticRetrievalOptions {
  maxRounds?: number;
}

export interface AgenticRetrievalResult extends RepoContextBuildResult {
  retrievalRounds: number;
  sufficiencyReason: string;
  taskKind: RepoTaskKind;
}

const EDIT_WORDS = [
  'edit', 'update', 'change', 'modify', 'fix', 'refactor', 'improve',
  'add', 'remove', 'delete', 'implement', 'rewrite', 'patch', 'create',
];

const DEBUG_WORDS = ['slow', 'bug', 'error', 'broken', 'fail', 'crash', 'issue', 'wrong', 'debug'];

const EXPLAIN_WORDS = ['explain', 'what', 'how', 'why', 'describe', 'overview', 'walk through'];

export function classifyRepoTask(message: string): RepoTaskKind {
  const lower = message.toLowerCase();
  if (EDIT_WORDS.some(w => new RegExp(`\\b${w}\\b`, 'i').test(lower))) return 'edit';
  if (DEBUG_WORDS.some(w => new RegExp(`\\b${w}\\b`, 'i').test(lower))) return 'debug';
  if (EXPLAIN_WORDS.some(w => new RegExp(`\\b${w}\\b`, 'i').test(lower))) return 'explain';
  return 'explain';
}

function hasHighConfidencePath(result: RepoContextBuildResult): boolean {
  return result.candidates.some(
    c => result.resolvedPaths.includes(c.path) && c.confidence >= 0.85
  );
}

export function assessContextSufficiency(
  result: RepoContextBuildResult,
  task: RepoTaskKind
): { sufficient: boolean; reason: string } {
  const fullFileCount = result.fullFiles.length;
  const graphNeighborCount = result.graphExpandedPaths.length;
  const ragWithBody = result.ragResults.filter(
    r => !r.file_path.startsWith('[') && r.chunk_text && !r.chunk_text.includes('[SIGNATURES]')
  );
  const topSimilarity = ragWithBody.reduce((max, r) => Math.max(max, r.similarity ?? 0), 0);

  if (task === 'edit') {
    if (fullFileCount >= 1) {
      return { sufficient: true, reason: 'Edit task has full file content (GitHub or repo index).' };
    }
    if (hasHighConfidencePath(result)) {
      return { sufficient: false, reason: 'Edit task has target path but no full file content yet.' };
    }
    return { sufficient: false, reason: 'Edit task missing full file or high-confidence path.' };
  }

  if (task === 'debug') {
    if (fullFileCount >= 2 || (fullFileCount >= 1 && graphNeighborCount >= 1)) {
      return { sufficient: true, reason: 'Debug task has multiple related files.' };
    }
    if (hasHighConfidencePath(result) && graphNeighborCount >= 1) {
      return { sufficient: true, reason: 'Debug task has target plus graph neighbor.' };
    }
    return { sufficient: false, reason: 'Debug task needs more related files.' };
  }

  if (topSimilarity >= 0.35 || ragWithBody.length >= 2) {
    return { sufficient: true, reason: 'Explain task has sufficient RAG excerpts.' };
  }
  return { sufficient: false, reason: 'Explain task needs better RAG matches.' };
}

function extractRetrievalNouns(message: string): string[] {
  const tokens = message.match(/\b[A-Za-z][A-Za-z0-9_-]{2,}\b/g) ?? [];
  const stop = new Set([
    'the', 'this', 'that', 'with', 'from', 'into', 'your', 'please', 'help',
    'file', 'code', 'repo', 'repository', 'function', 'class', 'method',
  ]);
  return [...new Set(tokens.filter(t => !stop.has(t.toLowerCase())))].slice(0, 8);
}

export function expandRetrievalQuery(message: string, partialResult: RepoContextBuildResult): string {
  const nouns = extractRetrievalNouns(message);
  const paths = partialResult.resolvedPaths.slice(0, 3);
  const ragPaths = partialResult.ragResults
    .filter(r => !r.file_path.startsWith('['))
    .slice(0, 3)
    .map(r => r.file_path.split('/').pop() ?? r.file_path);
  return [...nouns, ...paths.map(p => p.split('/').pop() ?? p), ...ragPaths]
    .filter(Boolean)
    .join(' ');
}

export async function buildRepoContextWithAgenticRetrieval(
  input: RepoContextBuildInput,
  options: AgenticRetrievalOptions = {}
): Promise<AgenticRetrievalResult> {
  const maxRounds = options.maxRounds ?? 2;
  const taskKind = classifyRepoTask(input.message);

  let result = await buildRepoContext(input);
  let assessment = assessContextSufficiency(result, taskKind);
  let rounds = 1;

  while (!assessment.sufficient && rounds < maxRounds) {
    const expandedQuery = expandRetrievalQuery(input.message, result);
    if (!expandedQuery.trim()) break;

    try {
      const moreRag = await searchRepo(expandedQuery, input.owner, input.repo, {
        limit: 20,
        hybridSearch: true,
      });

      const mergedRag = [...result.ragResults];
      const seenKeys = new Set(mergedRag.map(r => `${r.file_path}::${r.chunk_index}`));
      for (const r of moreRag) {
        const k = `${r.file_path}::${r.chunk_index}`;
        if (!seenKeys.has(k)) {
          seenKeys.add(k);
          mergedRag.push(r);
        }
      }
      result = { ...result, ragResults: mergedRag };
    } catch (err) {
      console.warn('[Agentic Retrieval] Round expansion failed:', err);
      break;
    }

    rounds += 1;
    assessment = assessContextSufficiency(result, taskKind);
  }

  if (
    !assessment.sufficient &&
    taskKind === 'edit' &&
    result.decision.isEditIntent &&
    result.fullFiles.length === 0
  ) {
    result = {
      ...result,
      decision: {
        ...result.decision,
        mode: 'clarification_required',
        reason: assessment.reason,
      },
      allowRepoEdit: false,
    };
  }

  return {
    ...result,
    retrievalRounds: rounds,
    sufficiencyReason: assessment.reason,
    taskKind,
  };
}
